import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Radio } from "lucide-react";
import { api, apiBase, type APIKey } from "@/lib/api";
import {
  buildRealtimeWsUrl,
  formatRealtimeEventLine,
  parseRealtimeMessage,
} from "@/lib/realtime";
import { ErrorBox, PageHeader, Spinner } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type ConnState = "idle" | "connecting" | "open" | "closed" | "error";

export function RealtimePage() {
  const { id = "" } = useParams<{ id: string }>();
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [keyId, setKeyId] = useState("");
  const [revealedKey, setRevealedKey] = useState("");
  const [tables, setTables] = useState("");
  const [state, setState] = useState<ConnState>("idle");
  const [events, setEvents] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const { data } = await api.listKeys(id);
        setKeys(data ?? []);
        const pub =
          data?.find((k) => k.key_type === "public" && !k.revoked_at) ??
          data?.find((k) => !k.revoked_at);
        if (pub) setKeyId(pub.id);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [id]);

  async function ensureKeyMaterial(): Promise<string> {
    if (revealedKey.trim()) return revealedKey.trim();
    if (!keyId) throw new Error("Select a project key");
    setBusy(true);
    try {
      const { data } = await api.revealKey(id, keyId);
      setRevealedKey(data.key);
      return data.key;
    } finally {
      setBusy(false);
    }
  }

  async function connect() {
    setError(null);
    try {
      const apikey = await ensureKeyMaterial();
      const url = buildRealtimeWsUrl({
        apiBaseUrl: apiBase(),
        apiKey: apikey,
        tables: tables.trim() || undefined,
      });
      wsRef.current?.close();
      setState("connecting");
      setEvents((prev) => [
        `[local] connecting ${url.replace(/apikey=[^&]+/, "apikey=***")}`,
        ...prev,
      ].slice(0, 100));
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        setState("open");
        setEvents((prev) => [`[local] open`, ...prev].slice(0, 100));
      };
      ws.onclose = () => {
        setState("closed");
        setEvents((prev) => [`[local] closed`, ...prev].slice(0, 100));
      };
      ws.onerror = () => {
        setState("error");
        setEvents((prev) => [`[local] error`, ...prev].slice(0, 100));
      };
      ws.onmessage = (ev) => {
        const text = String(ev.data);
        const msg = parseRealtimeMessage(text);
        const line = msg
          ? formatRealtimeEventLine(msg)
          : text.slice(0, 200);
        setEvents((prev) => [line, ...prev].slice(0, 100));
      };
    } catch (e) {
      setError(e);
      setState("error");
    }
  }

  function disconnect() {
    wsRef.current?.close();
    wsRef.current = null;
    setState("closed");
  }

  if (loading) return <Spinner label="Loading realtime…" />;

  const previewUrl = buildRealtimeWsUrl({
    apiBaseUrl: apiBase(),
    apiKey: "YOUR_PROJECT_KEY",
    tables: tables.trim() || undefined,
  });

  return (
    <div>
      <PageHeader
        eyebrow="Live"
        title="Realtime"
        description="Subscribe to project table change events over WebSocket (/v1/realtime). Ownership filters apply when using an app user JWT; public key receives unowned rows only."
        actions={
          <Badge
            variant={state === "open" ? "default" : "secondary"}
            className="gap-1"
          >
            <Radio className="h-3 w-3" strokeWidth={1.75} />
            {state}
          </Badge>
        }
      />

      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="card mb-6 space-y-4 p-5">
        <div>
          <h2 className="mb-1 font-sans text-sm font-semibold text-text-primary">
            Connection
          </h2>
          <p className="text-xs text-text-muted">
            Uses an owner-revealed project key as{" "}
            <code className="font-mono">apikey</code> query param. Key material
            stays in memory for this session only.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-text-muted">
            Project key
            <select
              className="input text-sm"
              value={keyId}
              onChange={(e) => {
                setKeyId(e.target.value);
                setRevealedKey("");
              }}
            >
              <option value="">Select…</option>
              {keys
                .filter((k) => !k.revoked_at)
                .map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.key_type} · {k.id.slice(0, 8)}…
                    {k.revealable ? "" : " (legacy)"}
                  </option>
                ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-text-muted">
            Tables filter (optional)
            <input
              className="input min-w-[14rem] text-sm"
              value={tables}
              onChange={(e) => setTables(e.target.value)}
              placeholder="todos,profiles"
            />
          </label>
          <Button
            type="button"
            size="sm"
            disabled={busy || state === "connecting" || state === "open"}
            onClick={() => void connect()}
          >
            Connect
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={state !== "open" && state !== "connecting"}
            onClick={() => disconnect()}
          >
            Disconnect
          </Button>
        </div>
        <div>
          <p className="mb-1 text-xs text-text-muted">URL template</p>
          <code className="block break-all rounded-md bg-surface-container-low px-2 py-1.5 font-mono text-[11px]">
            {previewUrl}
          </code>
        </div>
      </div>

      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="font-sans text-sm font-semibold text-text-primary">
            Event log
          </h2>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setEvents([])}
          >
            Clear
          </Button>
        </div>
        {events.length === 0 ? (
          <p className="text-sm text-text-muted">
            No events yet. Connect, then mutate rows via Data API or SQL to see
            INSERT/UPDATE/DELETE fanout.
          </p>
        ) : (
          <ul className="max-h-80 space-y-1 overflow-auto font-mono text-[11px] text-text-muted">
            {events.map((line, i) => (
              <li key={`${i}-${line.slice(0, 24)}`} className="border-b border-border-subtle py-1">
                {line}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
